import { CheckIcon, CloudRainIcon, InfoIcon, SirenIcon, TriangleAlertIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { VariableTextarea } from '@/components/VariableTextarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { useTags } from '@/features/contacts/queries';
import { errorMessage } from '@/lib/api';
import { useDebounce } from '@/lib/hooks';
import type { AutomationOverview, AutomationRuleDto, AutomationRuleInput, ScheduleTrigger, WeatherAlertCategory, WeatherAlertSeverity, WeatherMetric, WeatherTrigger } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useRulePreview } from './queries';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const VARIABLES: Array<{ token: string; hint: string; weatherOnly?: boolean; alertOnly?: boolean }> = [
  { token: '{{job.name}}', hint: 'Job name' },
  { token: '{{date}}', hint: 'Today, e.g. Tue, Sep 22' },
  { token: '{{time}}', hint: 'Time now' },
  { token: '{{weather.alert}}', hint: 'The alert, e.g. Tornado Warning', alertOnly: true },
  { token: '{{weather.alertUntil}}', hint: 'When the alert ends', alertOnly: true },
  { token: '{{weather.alertSource}}', hint: 'Issuing office, e.g. NWS Melbourne FL', alertOnly: true },
  { token: '{{weather.alertHeadline}}', hint: 'First line of the alert text', alertOnly: true },
  { token: '{{weather.summary}}', hint: 'One-line forecast', weatherOnly: true },
  { token: '{{weather.rainChance}}', hint: 'Peak chance of rain (%)', weatherOnly: true },
  { token: '{{weather.rainStartsAt}}', hint: 'Hour rain is likeliest', weatherOnly: true },
  { token: '{{weather.heatIndex}}', hint: 'Peak heat index (°F)', weatherOnly: true },
  { token: '{{weather.high}}', hint: "Today's high (°F)", weatherOnly: true },
  { token: '{{weather.low}}', hint: "Today's low (°F)", weatherOnly: true },
  { token: '{{weather.wind}}', hint: 'Peak wind / gust (mph)', weatherOnly: true },
  { token: '{{weather.thunderstormAt}}', hint: 'Hour storms are expected', weatherOnly: true },
];

interface Preset {
  label: string;
  text: string;
  trigger?: Partial<WeatherTrigger>;
  /** Safety notices default to ignoring quiet hours. */
  urgent?: boolean;
}

const PRESETS: Record<'weather' | 'schedule', Preset[]> = {
  weather: [
    { label: 'Tornado warning', urgent: true, text: 'TORNADO WARNING for {{job.name}} from {{weather.alertSource}} until {{weather.alertUntil}}. Stop work now and get to the designated shelter. Stay away from windows, equipment and the open deck until the all-clear.', trigger: { metric: 'alert', alertCategories: ['tornado'], alertSeverities: ['warning'], lookaheadHours: 6 } },
    { label: 'Flood', urgent: true, text: '{{weather.alert}} for {{job.name}} until {{weather.alertUntil}}. Keep out of trenches and low areas, move equipment to high ground, and do not drive through standing water.', trigger: { metric: 'alert', alertCategories: ['flood'], alertSeverities: ['warning', 'watch'], lookaheadHours: 12 } },
    { label: 'Severe storm', urgent: true, text: '{{weather.alert}} for {{job.name}} until {{weather.alertUntil}}. Secure loose materials, lower booms, no work at height. Shelter in vehicles or the trailer.', trigger: { metric: 'alert', alertCategories: ['severe_thunderstorm', 'tornado'], alertSeverities: ['warning', 'watch'], lookaheadHours: 6 } },
    { label: 'Hurricane / tropical', text: '{{weather.alert}} for {{job.name}} ({{weather.alertSource}}). Follow the storm plan: tie down, secure the site, and watch for the next update from the superintendent.', trigger: { metric: 'alert', alertCategories: ['hurricane'], alertSeverities: ['warning', 'watch'], lookaheadHours: 48 } },
    { label: 'Winter storm', text: '{{weather.alert}} at {{job.name}} until {{weather.alertUntil}}. Expect a delayed start; check for an update by 6 AM before you travel.', trigger: { metric: 'alert', alertCategories: ['winter', 'extreme_cold'], alertSeverities: ['warning', 'watch', 'advisory'], lookaheadHours: 24 } },
    { label: 'Any warning', urgent: true, text: 'Weather alert for {{job.name}}: {{weather.alert}} ({{weather.alertSource}}) until {{weather.alertUntil}}. {{weather.alertHeadline}}', trigger: { metric: 'alert', alertCategories: [], alertSeverities: ['warning'], lookaheadHours: 12 } },
    { label: 'Rain delay', text: 'Weather: {{weather.rainChance}}% chance of rain at {{job.name}} around {{weather.rainStartsAt}}. Plan for a delayed start; watch for an update by 6 AM.', trigger: { metric: 'rain_probability', op: '>=', value: 60 } },
    { label: 'Heat safety', urgent: true, text: 'Heat advisory for {{job.name}}: heat index up to {{weather.heatIndex}}°F today. Water, rest, shade every hour. Report heat illness symptoms to your foreman immediately.', trigger: { metric: 'heat_index', op: '>=', value: 103 } },
    { label: 'Lightning', urgent: true, text: 'Lightning expected near {{job.name}} around {{weather.thunderstormAt}}. Crane and elevated work stop at the first strike within 10 miles. Shelter in vehicles or the trailer until 30 minutes after the last strike.', trigger: { metric: 'lightning', op: '>=', value: 1 } },
    { label: 'High wind', text: 'Wind gusts to {{weather.wind}} mph forecast at {{job.name}}. No crane picks or work on open decks until the superintendent clears it.', trigger: { metric: 'wind_speed', op: '>=', value: 35 } },
    { label: 'Freeze', text: 'Low of {{weather.low}}°F tonight at {{job.name}}. Drain hoses and protect fresh concrete before you leave.', trigger: { metric: 'temperature_low', op: '<=', value: 32 } },
  ],
  schedule: [
    { label: 'Daily start', text: 'Good morning — {{job.name}} starts at 7:00 AM at the north gate. Hard hats, vests and badges required. Today: {{weather.summary}}' },
    { label: 'Timesheets', text: 'Reminder: {{job.name}} timesheets are due by 5 PM today. Turn them in at the trailer or text your foreman a photo.' },
    { label: 'Inspection day', text: 'Inspection at {{job.name}} tomorrow morning. Clear walkways, cover open trenches and have your permits at hand.' },
  ],
};

const SEVERITY_CHOICES: Array<{ value: WeatherAlertSeverity[]; label: string; hint: string }> = [
  { value: ['warning'], label: 'Warnings only', hint: 'Happening or imminent' },
  { value: ['warning', 'watch'], label: 'Warnings and watches', hint: 'Also when conditions are favourable' },
  { value: ['warning', 'watch', 'advisory', 'statement'], label: 'Everything', hint: 'Advisories and statements too' },
];

export interface RuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobName: string;
  kind: 'weather' | 'schedule';
  rule?: AutomationRuleDto | null;
  overview: AutomationOverview;
  onSubmit: (body: AutomationRuleInput, id: string | null) => Promise<unknown>;
  busy?: boolean;
}

/**
 * Create or edit a rule. Weather rules choose between an official alert (tornado, flood, severe storm, hurricane,
 * winter, heat…) and a forecast threshold, then say what to send and to whom; the dialog evaluates the condition
 * against the live forecast as you go, so you can see whether it would fire right now.
 */
export function RuleDialog({ open, onOpenChange, jobId, jobName, kind, rule, overview, onSubmit, busy }: RuleDialogProps) {
  const editing = !!rule;
  const d = overview.defaults;
  const tags = useTags(jobId);
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [weather, setWeather] = useState<WeatherTrigger>({ metric: 'rain_probability', op: '>=', value: d.rainProbability, lookaheadHours: d.lookaheadHours });
  const [schedule, setSchedule] = useState<ScheduleTrigger>({ frequency: 'weekly', time: '06:30', weekdays: [1, 2, 3, 4, 5] });
  const [template, setTemplate] = useState('');
  const [audienceType, setAudienceType] = useState<'all' | 'tags'>('all');
  const [audienceTags, setAudienceTags] = useState<string[]>([]);
  const [mode, setMode] = useState<'draft' | 'auto'>(d.defaultMode);
  const [bypass, setBypass] = useState(false);
  const [translate, setTranslate] = useState(true);
  const [cooldown, setCooldown] = useState(720);
  const [error, setError] = useState<string | null>(null);

  const applyPreset = (p: Preset) => {
    setTemplate(p.text);
    if (!editing) setName(p.label);
    if (p.trigger) setWeather((w) => ({ ...w, ...p.trigger, op: p.trigger?.op ?? (p.trigger?.metric === 'alert' ? '>=' : w.op), value: p.trigger?.value ?? (p.trigger?.metric === 'alert' ? 1 : w.value) }));
    if (p.urgent !== undefined) setBypass(p.urgent);
    if (p.trigger?.metric === 'alert') setCooldown(180);
  };

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (rule) {
      setName(rule.name);
      setEnabled(rule.enabled);
      if (rule.kind === 'weather') setWeather(rule.trigger as WeatherTrigger);
      else setSchedule(rule.trigger as ScheduleTrigger);
      setTemplate(rule.action.template);
      setAudienceType(rule.action.audience.type);
      setAudienceTags(rule.action.audience.type === 'tags' ? rule.action.audience.tags : []);
      setMode(rule.mode);
      setBypass(rule.bypassQuietHours);
      setTranslate(rule.action.translate ?? true);
      setCooldown(rule.cooldownMinutes);
    } else {
      const first = PRESETS[kind][0];
      setName(first?.label ?? '');
      setEnabled(true);
      setWeather({ metric: 'rain_probability', op: '>=', value: d.rainProbability, lookaheadHours: d.lookaheadHours, ...(first?.trigger ?? {}) });
      setSchedule({ frequency: 'weekly', time: '06:30', weekdays: [1, 2, 3, 4, 5] });
      setTemplate(first?.text ?? '');
      setAudienceType('all');
      setAudienceTags([]);
      setMode(d.defaultMode);
      setBypass(!!first?.urgent);
      setTranslate(true);
      setCooldown(first?.trigger?.metric === 'alert' ? 180 : 720);
    }
  }, [open, rule, kind, d]);

  const isAlert = kind === 'weather' && weather.metric === 'alert';
  const debouncedTemplate = useDebounce(template, 400);
  const preview = useRulePreview(jobId, debouncedTemplate, weather.lookaheadHours, kind === 'weather' ? weather : undefined, open);
  const metricInfo = useMemo(() => overview.metrics.find((m) => m.metric === weather.metric), [overview.metrics, weather.metric]);
  const thresholdMetrics = overview.metrics.filter((m) => m.metric !== 'alert');
  const providerHasAlerts = overview.weatherProvider?.alerts ?? true;

  const setKindOfCondition = (alert: boolean) => {
    if (alert) setWeather((w) => ({ ...w, metric: 'alert', op: '>=', value: 1, alertCategories: w.alertCategories ?? [], alertSeverities: w.alertSeverities ?? ['warning', 'watch'] }));
    else {
      const m = thresholdMetrics[0];
      setWeather((w) => ({ metric: m?.metric ?? 'rain_probability', op: m?.defaultOp ?? '>=', value: d.rainProbability, lookaheadHours: w.lookaheadHours }));
    }
  };
  const toggleCategory = (c: WeatherAlertCategory) =>
    setWeather((w) => {
      const cur = w.alertCategories ?? [];
      return { ...w, alertCategories: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] };
    });
  const severityIndex = SEVERITY_CHOICES.findIndex((c) => sameSet(c.value, weather.alertSeverities ?? ['warning', 'watch']));

  const insertVar = (token: string) => setTemplate((t) => (t.endsWith(' ') || t.length === 0 ? `${t}${token}` : `${t} ${token}`));

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError('Give the rule a name');
    if (!template.trim()) return setError('Write the message template');
    if (audienceType === 'tags' && audienceTags.length === 0) return setError('Pick at least one tag');
    if (kind === 'schedule' && schedule.frequency === 'weekly' && (schedule.weekdays?.length ?? 0) === 0) return setError('Pick at least one weekday');
    if (kind === 'schedule' && schedule.frequency === 'once' && !schedule.date) return setError('Pick a date');
    const trigger: WeatherTrigger = isAlert ? { metric: 'alert', op: '>=', value: 1, lookaheadHours: weather.lookaheadHours, alertCategories: weather.alertCategories ?? [], alertSeverities: weather.alertSeverities ?? ['warning', 'watch'] } : { metric: weather.metric, op: weather.op, value: weather.value, lookaheadHours: weather.lookaheadHours };
    const body: AutomationRuleInput = {
      name: name.trim(),
      kind,
      enabled,
      trigger: kind === 'weather' ? trigger : schedule,
      action: { template: template.trim(), audience: audienceType === 'all' ? { type: 'all' } : { type: 'tags', tags: audienceTags }, translate },
      mode,
      bypassQuietHours: bypass,
      cooldownMinutes: cooldown,
      // Alerts are time-critical; forecast thresholds change slowly.
      evaluateEveryMinutes: isAlert ? 15 : 30,
    };
    try {
      await onSubmit(body, rule?.id ?? null);
      toast.success(editing ? 'Rule updated' : 'Rule created');
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const toggleDay = (day: number) => setSchedule((s) => ({ ...s, weekdays: (s.weekdays ?? []).includes(day) ? (s.weekdays ?? []).filter((x) => x !== day) : [...(s.weekdays ?? []), day].sort() }));
  const evaluation = preview.data?.evaluation;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-normal tracking-tight">{editing ? `Edit ${rule?.name}` : kind === 'weather' ? 'New weather rule' : 'New scheduled reminder'}</DialogTitle>
          <DialogDescription>
            {kind === 'weather'
              ? `Checked against the forecast and the official alerts for ${jobName}: every 15 minutes for alerts, every 30 for thresholds. When the condition is met, the message is drafted for a job admin or sent automatically.`
              : `Fires on a schedule in ${jobName}'s local time. Drafts wait for a job admin; automatic sends go out on the dot (quiet hours push them to the morning).`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5">
          <FormError message={error} />
          {!editing && (
            <div className="grid gap-1.5">
              <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Start from</span>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS[kind].map((p) => (
                  <Button key={p.label} type="button" variant="outline" size="xs" onClick={() => applyPreset(p)} className={cn(p.trigger?.metric === 'alert' && 'border-destructive/40')}>
                    {p.trigger?.metric === 'alert' ? <SirenIcon className="text-destructive" /> : null}
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <Field label="Rule name" htmlFor="rule-name" required>
              <Input id="rule-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
            </Field>
            <div className="flex items-center gap-3 self-end pb-1">
              <Switch id="rule-enabled" checked={enabled} onCheckedChange={setEnabled} />
              <Label htmlFor="rule-enabled" className="font-normal">
                {enabled ? 'Enabled' : 'Paused'}
              </Label>
            </div>
          </div>

          {kind === 'weather' ? (
            <div className="grid gap-4 border p-4">
              <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Kind of condition">
                <ConditionTile on={isAlert} onClick={() => setKindOfCondition(true)} icon={SirenIcon} title="Official alert" hint="Tornado, flood, severe storm, hurricane, winter, heat… as issued by the weather service" />
                <ConditionTile on={!isAlert} onClick={() => setKindOfCondition(false)} icon={CloudRainIcon} title="Forecast threshold" hint="Rain chance, heat index, wind, thunderstorms, temperature" />
              </div>

              {isAlert ? (
                <>
                  {!providerHasAlerts && (
                    <Alert variant="warning">
                      <TriangleAlertIcon />
                      <AlertDescription>Official weather alerts are not enabled for this organization yet, so this rule will not fire until they are. Rain, heat, wind and lightning thresholds work as usual.</AlertDescription>
                    </Alert>
                  )}
                  <div className="grid gap-1.5">
                    <span className="text-sm font-medium">Which alerts</span>
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Alert kinds">
                      <button type="button" aria-pressed={!(weather.alertCategories?.length)} onClick={() => setWeather((w) => ({ ...w, alertCategories: [] }))} className={cn('flex h-8 items-center gap-1.5 border px-2.5 text-xs transition-colors', !weather.alertCategories?.length ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:border-foreground/30')}>
                        {!weather.alertCategories?.length && <CheckIcon className="size-3" aria-hidden />} Any kind
                      </button>
                      {overview.alertCategories.map((c) => {
                        const on = weather.alertCategories?.includes(c.category) ?? false;
                        return (
                          <button key={c.category} type="button" aria-pressed={on} onClick={() => toggleCategory(c.category)} title={`${c.description} e.g. ${c.examples}`} className={cn('flex h-8 items-center gap-1.5 border px-2.5 text-xs transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:border-foreground/30')}>
                            {on && <CheckIcon className="size-3" aria-hidden />}
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                    <div className="grid gap-1.5">
                      <span className="text-sm font-medium">How serious</span>
                      <div role="radiogroup" aria-label="Severity" className="bg-card inline-flex h-9 w-fit items-stretch border text-xs">
                        {SEVERITY_CHOICES.map((c, i) => (
                          <button key={c.label} type="button" role="radio" aria-checked={severityIndex === i} title={c.hint} onClick={() => setWeather((w) => ({ ...w, alertSeverities: c.value }))} className={cn('border-r px-3 font-medium transition-colors last:border-r-0', severityIndex === i ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      in force or starting within
                      <Input type="number" min={1} max={72} value={weather.lookaheadHours} onChange={(e) => setWeather((w) => ({ ...w, lookaheadHours: Math.max(1, Math.min(72, Number(e.target.value) || 1)) }))} className="w-16" aria-label="Lookahead hours" />
                      h
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto_120px_auto]">
                    <select value={weather.metric} onChange={(e) => setWeather((w) => ({ ...w, metric: e.target.value as WeatherMetric, op: overview.metrics.find((m) => m.metric === e.target.value)?.defaultOp ?? '>=' }))} className="border-input bg-background h-9 border px-2 text-sm" aria-label="Metric">
                      {thresholdMetrics.map((m) => (
                        <option key={m.metric} value={m.metric}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    {weather.metric === 'lightning' ? (
                      <span className="text-muted-foreground self-center text-sm sm:col-span-2">any thunderstorm hour</span>
                    ) : (
                      <>
                        <select value={weather.op} onChange={(e) => setWeather((w) => ({ ...w, op: e.target.value as '>=' | '<=' }))} className="border-input bg-background h-9 border px-2 text-sm" aria-label="Comparison">
                          <option value=">=">at least</option>
                          <option value="<=">at most</option>
                        </select>
                        <div className="relative">
                          <Input type="number" value={weather.value} onChange={(e) => setWeather((w) => ({ ...w, value: Number(e.target.value) }))} className="pr-10" aria-label="Threshold" />
                          <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs">{metricInfo?.unit}</span>
                        </div>
                      </>
                    )}
                    <label className="flex items-center gap-2 text-sm">
                      in the next
                      <Input type="number" min={1} max={72} value={weather.lookaheadHours} onChange={(e) => setWeather((w) => ({ ...w, lookaheadHours: Math.max(1, Math.min(72, Number(e.target.value) || 1)) }))} className="w-16" aria-label="Lookahead hours" />
                      h
                    </label>
                  </div>
                  <p className="text-muted-foreground text-xs">{metricInfo?.description}</p>
                </>
              )}

              {evaluation && (
                <div className={cn('flex items-start gap-2 border-l-2 pl-3 text-xs', evaluation.fired ? 'border-warning text-foreground' : 'border-border text-muted-foreground')}>
                  {evaluation.fired ? <TriangleAlertIcon className="text-warning mt-0.5 size-3.5 shrink-0" aria-hidden /> : <InfoIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />}
                  <span>
                    <span className="font-medium">Right now:</span> {evaluation.detail} {evaluation.fired ? 'This rule would fire.' : 'It would not fire.'}
                  </span>
                </div>
              )}
              <label className="flex flex-wrap items-center gap-2 text-sm">
                Don&apos;t fire again for
                <Input type="number" min={0} max={10080} value={cooldown} onChange={(e) => setCooldown(Math.max(0, Number(e.target.value) || 0))} className="w-24" aria-label="Cooldown minutes" />
                minutes after it fires
                <span className="text-muted-foreground text-xs">{isAlert ? '(a fresh alert of the same kind inside this window is not re-sent)' : ''}</span>
              </label>
            </div>
          ) : (
            <div className="grid gap-3 border p-4">
              <h4 className="text-sm font-medium">When</h4>
              <div className="grid gap-3 sm:grid-cols-[160px_130px_1fr]">
                <select value={schedule.frequency} onChange={(e) => setSchedule((s) => ({ ...s, frequency: e.target.value as ScheduleTrigger['frequency'] }))} className="border-input bg-background h-9 border px-2 text-sm" aria-label="Frequency">
                  <option value="weekly">Weekly</option>
                  <option value="daily">Daily</option>
                  <option value="once">Once</option>
                </select>
                <Input type="time" value={schedule.time} onChange={(e) => setSchedule((s) => ({ ...s, time: e.target.value }))} aria-label="Time" />
                {schedule.frequency === 'weekly' ? (
                  <div className="flex flex-wrap items-center gap-1">
                    {WEEKDAYS.map((label, day) => (
                      <button key={day} type="button" onClick={() => toggleDay(day)} className={cn('h-9 w-11 border text-xs font-medium transition-colors', (schedule.weekdays ?? []).includes(day) ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent')} aria-pressed={(schedule.weekdays ?? []).includes(day)}>
                        {label}
                      </button>
                    ))}
                  </div>
                ) : schedule.frequency === 'once' ? (
                  <Input type="date" value={schedule.date ?? ''} onChange={(e) => setSchedule((s) => ({ ...s, date: e.target.value }))} aria-label="Date" />
                ) : (
                  <span className="text-muted-foreground self-center text-xs">Every day</span>
                )}
              </div>
              <p className="text-muted-foreground text-xs">Times are in the job&apos;s timezone ({overview.location.timezone ?? overview.quietHours.timezone}).</p>
            </div>
          )}

          <Field label="Message" htmlFor="rule-template" required description="Click a variable to insert it. Quiet-hours and opt-out rules apply like any other send.">
            <VariableTextarea id="rule-template" rows={4} value={template} onChange={(e) => setTemplate(e.target.value)} maxLength={2000} className="text-base leading-relaxed" />
            <div className="flex flex-wrap gap-1">
              {VARIABLES.filter((v) => (!v.weatherOnly || kind === 'weather' || overview.location.latitude !== null) && (!v.alertOnly || isAlert)).map((v) => (
                <button key={v.token} type="button" onClick={() => insertVar(v.token)} className="border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground border px-1.5 py-0.5 font-mono text-[11px]" title={v.hint}>
                  {v.token}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid gap-2">
            <Label>Preview with today&apos;s forecast</Label>
            <div className="bg-muted/40 border p-3 text-sm whitespace-pre-wrap">{preview.data?.body || <span className="text-muted-foreground italic">Type a message to preview it</span>}</div>
            {preview.data?.forecastSummary && <p className="text-muted-foreground text-xs">Forecast: {preview.data.forecastSummary}</p>}
            {isAlert && !evaluation?.fired && <p className="text-muted-foreground text-xs">Alert variables are blank until an alert is in force; the preview shows the text as it would read with none.</p>}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>Send to</Label>
              <RadioGroup value={audienceType} onValueChange={(v) => setAudienceType(v as 'all' | 'tags')} className="gap-2">
                <label className={cn('flex items-center gap-3 border p-2.5 text-sm', audienceType === 'all' && 'border-primary')}>
                  <RadioGroupItem value="all" /> Everyone on this job
                </label>
                <label className={cn('flex items-start gap-3 border p-2.5 text-sm', audienceType === 'tags' && 'border-primary')}>
                  <RadioGroupItem value="tags" className="mt-0.5" />
                  <div className="grid flex-1 gap-2">
                    <span>Contacts with any of these tags</span>
                    {audienceType === 'tags' && (
                      <div className="flex flex-wrap gap-1">
                        {(tags.data?.items ?? []).map((t) => {
                          const on = audienceTags.includes(t.tag);
                          return (
                            <button key={t.tag} type="button" onClick={() => setAudienceTags(on ? audienceTags.filter((x) => x !== t.tag) : [...audienceTags, t.tag])} className={cn('border px-2 py-0.5 text-xs', on ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent')} aria-pressed={on}>
                              {t.tag} ({t.count})
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </label>
              </RadioGroup>
            </div>
            <div className="grid gap-2">
              <Label>What happens</Label>
              <RadioGroup value={mode} onValueChange={(v) => setMode(v as 'draft' | 'auto')} className="gap-2">
                <label className={cn('flex items-start gap-3 border p-2.5 text-sm', mode === 'draft' && 'border-primary')}>
                  <RadioGroupItem value="draft" className="mt-0.5" />
                  <span className="grid gap-0.5">
                    <span>Create a draft</span>
                    <span className="text-muted-foreground text-xs">A job admin reviews and sends it from History → Upcoming.</span>
                  </span>
                </label>
                <label className={cn('flex items-start gap-3 border p-2.5 text-sm', mode === 'auto' && 'border-primary')}>
                  <RadioGroupItem value="auto" className="mt-0.5" />
                  <span className="grid gap-0.5">
                    <span>Send automatically</span>
                    <span className="text-muted-foreground text-xs">{isAlert ? 'Goes out within minutes of the alert. Recommended for warnings.' : 'Goes out as soon as the rule fires.'}</span>
                  </span>
                </label>
              </RadioGroup>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={bypass} onCheckedChange={(v) => setBypass(v === true)} /> Send even during quiet hours (safety notices)
              </label>
              {overview.features.translation && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={translate} onCheckedChange={(v) => setTranslate(v === true)} /> Translate for each recipient&apos;s language
                </label>
              )}
            </div>
          </div>

          {mode === 'auto' && (
            <Alert variant="info">
              <InfoIcon />
              <AlertDescription>
                Automatic sends inside quiet hours ({overview.quietHours.start}–{overview.quietHours.end}) are held until the window ends unless you tick the safety override.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConditionTile({ on, onClick, icon: Icon, title, hint }: { on: boolean; onClick: () => void; icon: typeof SirenIcon; title: string; hint: string }) {
  return (
    <button type="button" role="radio" aria-checked={on} onClick={onClick} className={cn('flex items-start gap-3 border p-3 text-left transition-colors', on ? 'border-primary bg-accent/50' : 'hover:border-foreground/30 hover:bg-muted/40')}>
      <span className={cn('grid size-9 shrink-0 place-items-center border', on ? 'border-primary bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>{on ? <CheckIcon className="size-4" aria-hidden /> : <Icon className="size-4" aria-hidden />}</span>
      <span className="grid gap-0.5">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-muted-foreground text-xs leading-snug">{hint}</span>
      </span>
    </button>
  );
}

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}
